import { Loader2 } from 'lucide-react'
import { usePaymentMethods } from '@/hooks/use-payment-methods'
import { useUser } from '@/hooks/use-user'
import { formatShortCryptoAddress } from '@/utils/format'
import { DEFAULT_PAYMENT_METHOD } from '@/types/payment-methods'
import type { SelectablePaymentMethod, PaymentMethodType } from '@/types/payment-methods'
import CardBrandIcon from '@/components/CardBrandIcon'
import logoUrl from '@/assets/logo.png'

interface PaymentMethodSelectorProps {
	selectedMethod: SelectablePaymentMethod
	onSelect: (method: SelectablePaymentMethod) => void
	/** If set, visually indicate methods not matching this type are ineligible for current discount */
	restrictedToType?: PaymentMethodType | null
}

export default function PaymentMethodSelector({
	selectedMethod,
	onSelect,
	restrictedToType,
}: PaymentMethodSelectorProps) {
	const { cards, isLoading, isCardExpired } = usePaymentMethods()
	const { walletAddress } = useUser()

	const isSelected = (method: SelectablePaymentMethod) => {
		if (method.type === 'TOKEN' && selectedMethod.type === 'TOKEN') return true
		if (method.type === 'CARD' && selectedMethod.type === 'CARD') {
			return method.id === selectedMethod.id
		}
		return false
	}

	const formatExpiry = (month: number, year: number) => {
		return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`
	}

	return (
		<div className="space-y-2">
			<h4 className="font-figtree font-semibold text-sm text-white mb-1">
				Payment Method
			</h4>

			{/* SECH Wallet option */}
			<button
				type="button"
				onClick={() => onSelect(DEFAULT_PAYMENT_METHOD)}
				className={`w-full rounded-lg px-4 py-3 flex items-center gap-3 transition-colors border ${
					isSelected(DEFAULT_PAYMENT_METHOD)
						? 'border-[var(--pos-primary-color)] bg-[var(--pos-primary-background-color)]'
						: 'border-[var(--pos-input-border-color)] bg-[var(--pos-primary-background-color)] hover:bg-[var(--pos-primary-background-hover-color)]'
				} ${
					restrictedToType === 'CARD' ? 'opacity-50 cursor-not-allowed' : ''
				}`}
				disabled={restrictedToType === 'CARD'}
			>
				<div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
					isSelected(DEFAULT_PAYMENT_METHOD) ? 'border-[var(--pos-primary-color)]' : 'border-[var(--pos-input-border-color)]'
				}`}>
					{isSelected(DEFAULT_PAYMENT_METHOD) && (
						<div className="w-2.5 h-2.5 rounded-full bg-[var(--pos-primary-color)]" />
					)}
				</div>

				<img src={logoUrl} alt="SECH" className="h-7 w-7 rounded-full shrink-0" />

				<div className="text-left flex-1 min-w-0">
					<div className="font-figtree text-sm font-medium text-[var(--pos-primary-text-color)]">SECH Wallet</div>
					{walletAddress && (
						<div className="font-figtree text-xs text-[var(--pos-secondary-text-color)]">
							{formatShortCryptoAddress(walletAddress)}
						</div>
					)}
				</div>

				<span className="text-xs font-figtree font-medium text-[var(--pos-primary-color)] shrink-0">Default</span>
			</button>

			{/* Loading state */}
			{isLoading && (
				<div className="flex items-center justify-center py-2">
					<Loader2 className="h-4 w-4 animate-spin text-[var(--pos-secondary-text-color)]" />
				</div>
			)}

			{/* Saved cards */}
			{cards.map((card) => {
				const expired = isCardExpired(card)
				const disabled = expired || restrictedToType === 'TOKEN'

				return (
					<button
						key={card.id}
						type="button"
						onClick={() => {
							if (!disabled) onSelect(card)
						}}
						className={`w-full rounded-lg px-4 py-3 flex items-center gap-3 transition-colors border ${
							isSelected(card)
								? 'border-[var(--pos-primary-color)] bg-[var(--pos-primary-background-color)]'
								: 'border-[var(--pos-input-border-color)] bg-[var(--pos-primary-background-color)] hover:bg-[var(--pos-primary-background-hover-color)]'
						} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
						disabled={disabled}
					>
						<div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
							isSelected(card) ? 'border-[var(--pos-primary-color)]' : 'border-[var(--pos-input-border-color)]'
						}`}>
							{isSelected(card) && (
								<div className="w-2.5 h-2.5 rounded-full bg-[var(--pos-primary-color)]" />
							)}
						</div>

						<CardBrandIcon brand={card.cardBrand} width={36} className="shrink-0" />

						<div className="text-left flex-1 min-w-0">
							<div className="font-figtree text-sm font-medium text-[var(--pos-primary-text-color)]">
								•••• {card.lastFour}
							</div>
							<div className="font-figtree text-xs text-[var(--pos-secondary-text-color)]">
								Exp {formatExpiry(card.expiryMonth, card.expiryYear)}
							</div>
						</div>

						{expired && (
							<span className="text-xs font-figtree font-medium text-red-400 bg-red-400/10 px-2 py-0.5 rounded shrink-0">
								Expired
							</span>
						)}
					</button>
				)
			})}
		</div>
	)
}
